import { combineReducers } from 'redux';

import alternativeDates, * as fromAlternativeDates from './alternativeDates';
import filters, * as fromFilters from './filters';
import * as fromPartnerMetadata from './partnerMetadata';
import { partnerMetadataReducer as partnerMetadata } from './partnerMetadata';
import { priceBandsReducer as priceBands } from './priceBands/priceBandsReducer';
import * as fromPricePulse from './pricePulse';
import { pricePulseReducer as pricePulse } from './pricePulse';
import { railReducer as rail } from './rail/reducers';
import { singleLegResultsReducer as singleLegResults } from './singleLegResults/singleLegResultsReducer';
import selectedSort from './sort';

import type { AlternateDateItem } from '@web-platform/shared-universal-constants/src/alternativeDates';
import type { RailResultsState } from '@web-platform/shared-universal-types/src/redux/state/entities/rail';
import type { AlliancesFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/alliances';
import type { BaggageFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/baggage';
import type { CarriersFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/carriers';
import type { DepartureTimesState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/departureTimes';
import type { DurationFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/duration';
import type { EcoFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/eco';
import type { SelectedHotelsFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/hotelsFilter';
import type { MixedAirportFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/mixedAirport';
import type { FiltersState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/state';
import type { StopsFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/stops';
import type { SingleLegResultsState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/singleLegResults/singleLegResults';
import type { FeatureState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/flightSearchResults';
import type { PartnerMetadataTrustLevel } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/partnerMetadata/partnerMetadata';
import type { SelectedSortState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/sort/sort';
import type { PriceBandsState } from '@web-platform/shared-universal-types/src/redux/state/priceBands/priceBands';
import type { Nullable } from '@web-platform/shared-universal-types-utils/src/utils';

/*
  The root reducer for flight search results feature state, combining alternative dates, selected sort, partner metadata, price pulse, rail, filters, single leg results, and price bands.
 */

export default combineReducers({
  alternativeDates,
  selectedSort,
  partnerMetadata,
  pricePulse,
  rail,
  filters,
  singleLegResults,
  priceBands,
});

// sort
export const getSelectedSort = (state: FeatureState): SelectedSortState => state.selectedSort;

// partnerMetadata
export const getPartnerMetadata = (state: FeatureState, id: string): PartnerMetadataTrustLevel =>
  fromPartnerMetadata.get(state.partnerMetadata, id);
export const getAllPartnerMetadata = (
  state: FeatureState,
): Record<string, PartnerMetadataTrustLevel> => fromPartnerMetadata.getAll(state.partnerMetadata);

// rail
export const getRail = (state: FeatureState): RailResultsState => state.rail;

// pricePulse
export const getPricePulse = (state: FeatureState, itineraryId: string) =>
  fromPricePulse.get(state.pricePulse, itineraryId);
export const getPricePulseByIdMap = (state: FeatureState): fromPricePulse.PricePulseMap =>
  state.pricePulse.byId;

// single leg results
export const getSingleLegResults = (state: FeatureState): SingleLegResultsState =>
  state.singleLegResults;

// priceBands
export const getPriceBands = (state: FeatureState): PriceBandsState => state.priceBands;

// alternativeDates
export const getAlternativeDates = (state: FeatureState): Nullable<AlternateDateItem[]> =>
  fromAlternativeDates.getAlternativeDates(state.alternativeDates);

// filters
export const getFilters = (state: FeatureState): FiltersState => state.filters;
export const getStopsFilter = (state: FeatureState): StopsFilterState =>
  fromFilters.getStopsFilter(state.filters);
export const getAirportsFilter = (state: FeatureState) =>
  fromFilters.getAirportsFilter(state.filters);
export const getAirportsStatsFilter = (state: FeatureState) =>
  fromFilters.getAirportsStatsFilter(state.filters);
export const getFilterOrSortChangedCount = (state: FeatureState) =>
  state.filters.filterOrSortChangedCount;
export const getLayoversDurationFilter = (state: FeatureState) =>
  fromFilters.getLayoversDurationFilter(state.filters);
export const getLayoversExcludedAirportsFilter = (state: FeatureState) =>
  fromFilters.getLayoversExcludedAirportsFilter(state.filters);
export const getDepartureTimesFilter = (state: FeatureState): DepartureTimesState =>
  fromFilters.getDepartureTimesFilter(state.filters);
export const getDurationFilter = (state: FeatureState): DurationFilterState =>
  fromFilters.getDurationFilter(state.filters);
export const getCarriersFilter = (state: FeatureState): CarriersFilterState =>
  fromFilters.getCarriersFilter(state.filters);
export const getAlliancesFilter = (state: FeatureState): AlliancesFilterState =>
  fromFilters.getAlliancesFilter(state.filters);
export const getMixedAirportFilter = (state: FeatureState): MixedAirportFilterState =>
  fromFilters.getMixedAirportFilter(state.filters);
export const getHotelsFilter = (state: FeatureState): SelectedHotelsFilterState =>
  fromFilters.getHotelsFilter(state.filters);
export const getEcoFilter = (state: FeatureState): EcoFilterState =>
  fromFilters.getEcoFilter(state.filters);
export const getBaggageFilter = (state: FeatureState): BaggageFilterState =>
  fromFilters.getBaggageFilter(state.filters);
